import { subject } from '@casl/ability';
import {
    Box,
    Group,
    NavLink,
    SegmentedControl,
    Stack,
    Text,
} from '@mantine/core';
import {
    IconActivity,
    IconBriefcase,
    IconChartBar,
    IconDatabase,
    IconCode,
    IconDatabaseImport,
    IconFolders,
    IconHome,
    IconLayoutDashboard,
    IconMessages,
    IconPlugConnected,
    IconUsers,
    IconRouteAltLeft,
    IconMessageCircle,
    IconSearch,
    IconTable,
    IconTemplate,
    type Icon,
} from '@tabler/icons-react';
import { useState, type FC } from 'react';
import { Link, useLocation } from 'react-router';
import { AiAgentIcon } from '../../ee/features/aiCopilot/components/AiAgentIcon';
import { useAiAgentButtonVisibility } from '../../ee/features/aiCopilot/hooks/useAiAgentsButtonVisibility';
import { useCustomerIntelligenceEnabled } from '../../features/intelligence/hooks/useIntelligence';
import { LearnLink } from '../../features/learn/LearnLink';
import { useHasMetricsInCatalog } from '../../features/metricsCatalog/hooks/useMetricsCatalog';
import { useOptionalProjectRoute } from '../../hooks/useProjectRoute';
import useApp from '../../providers/App/useApp';
import MantineIcon from '../common/MantineIcon';
import classes from './AppSidebar.module.css';
import { AutopilotNavButton } from './AutopilotNavButton';
import BrowseMenu from './BrowseMenu';
import ExploreMenu from './ExploreMenu';
import HelpMenu from './HelpMenu';
import { INTELLIGENCE_GROUPS } from './intelligencePages';
import { MetricsLink } from './MetricsLink';
import {
    NavBarPortalContext,
    SIDEBAR_MENU_PORTAL_TARGET,
} from './NavBarPortalContext';
import SettingsMenu from './SettingsMenu';

type Item = { label: string; to: string; icon: Icon };

const GROUP_ICONS: Record<(typeof INTELLIGENCE_GROUPS)[number]['label'], Icon> =
    {
        Feedback: IconMessages,
        Customers: IconUsers,
        Signals: IconActivity,
        Sources: IconDatabaseImport,
    };

const SidebarLink: FC<Item & { pathname: string }> = ({
    label,
    to,
    icon,
    pathname,
}) => (
    <NavLink
        component={Link}
        to={to}
        label={label}
        active={pathname === to || pathname.startsWith(`${to}/`)}
        leftSection={<MantineIcon icon={icon} />}
        className={classes.link}
    />
);

export const AppSidebar: FC<{ projectUuid: string }> = ({ projectUuid }) => {
    const { pathname } = useLocation();
    const { user } = useApp();
    const isAiVisible = useAiAgentButtonVisibility();
    const projectRoute = useOptionalProjectRoute();
    const projectUrlIdentifier =
        projectRoute?.projectUrlIdentifier ?? projectUuid;
    const { data: hasMetrics } = useHasMetricsInCatalog({ projectUuid });
    const base = `/projects/${projectUuid}`;
    // Workflows and connectors need `manage`; showing them to viewers would link to a 403.
    const canManage =
        user.data?.ability?.can(
            'manage',
            subject('Project', {
                organizationUuid: user.data?.organizationUuid,
                projectUuid,
            }),
        ) ?? false;
    const intelligenceBase = `${base}/intelligence`;
    const showCustomerIntelligence = useCustomerIntelligenceEnabled();
    // Open on Work when the current page lives there, so its link stays visible.
    const [area, setArea] = useState<'work' | 'data'>(() =>
        pathname.startsWith(`${base}/workflows`) ||
        pathname.startsWith(`${base}/autopilot`) ||
        pathname.startsWith(`${base}/templates`)
            ? 'work'
            : 'data',
    );

    // Analyse first, then the content that analysis produces, then its sources.
    const canUseSqlRunner =
        user.data?.ability?.can(
            'manage',
            subject('SqlRunner', {
                organizationUuid: user.data?.organizationUuid,
                projectUuid,
            }),
        ) ?? false;
    const analyzeItems: Item[] = [
        { label: 'Explore', to: `${base}/tables`, icon: IconTable },
        ...(canUseSqlRunner
            ? [
                  {
                      label: 'SQL runner',
                      to: `${base}/sql-runner`,
                      icon: IconCode,
                  },
              ]
            : []),
    ];
    const contentItems: Item[] = [
        {
            label: 'Dashboards',
            to: `${base}/dashboards`,
            icon: IconLayoutDashboard,
        },
        { label: 'Charts', to: `${base}/saved`, icon: IconChartBar },
        { label: 'Spaces', to: `${base}/spaces`, icon: IconFolders },
    ];

    return (
        <Box
            component="nav"
            id="app-sidebar"
            className={classes.sidebar}
            aria-label="Project navigation"
        >
            {/* The navbar's own menus, moved here unchanged so their
                walkthrough markers move with them. Body portal keeps the
                dropdowns in the app theme instead of the dark navbar. */}
            <NavBarPortalContext.Provider value={SIDEBAR_MENU_PORTAL_TARGET}>
                <SegmentedControl
                    fullWidth
                    radius="xl"
                    aria-label="Sidebar section"
                    value={area}
                    onChange={(value) => setArea(value as 'work' | 'data')}
                    classNames={{
                        root: classes.switchRoot,
                        indicator: classes.switchIndicator,
                        label: classes.switchLabel,
                    }}
                    data={[
                        {
                            value: 'work',
                            label: (
                                <Group gap={6} justify="center" wrap="nowrap">
                                    <MantineIcon icon={IconBriefcase} />
                                    Work
                                </Group>
                            ),
                        },
                        {
                            value: 'data',
                            label: (
                                <Group gap={6} justify="center" wrap="nowrap">
                                    <MantineIcon icon={IconDatabase} />
                                    Data
                                </Group>
                            ),
                        },
                    ]}
                />

                <Stack gap={2} className={classes.quickActions}>
                    <Box className={classes.menuSlot}>
                        <ExploreMenu
                            projectUuid={projectUuid}
                            projectUrlIdentifier={projectUrlIdentifier}
                        />
                    </Box>
                    <NavLink
                        component={Link}
                        to={`${base}/ask`}
                        label="Ask"
                        active={pathname.startsWith(`${base}/ask`)}
                        leftSection={<MantineIcon icon={IconMessageCircle} />}
                        className={classes.link}
                    />
                    <NavLink
                        component={Link}
                        to={`${base}/search`}
                        label="Search"
                        active={pathname.startsWith(`${base}/search`)}
                        leftSection={<MantineIcon icon={IconSearch} />}
                        className={classes.link}
                    />
                    {isAiVisible && (
                        <NavLink
                            component={Link}
                            to={`${base}/ai-agents`}
                            label="Ask AI"
                            active={pathname.startsWith(`${base}/ai-agents`)}
                            leftSection={<AiAgentIcon size={16} />}
                            className={classes.link}
                        />
                    )}
                    <SidebarLink
                        label="Home"
                        to={`${base}/home`}
                        icon={IconHome}
                        pathname={pathname}
                    />
                </Stack>

                <Box className={classes.scroll}>
                    {area === 'data' && showCustomerIntelligence && (
                        <Box className={classes.group}>
                            <Text className={classes.section}>
                                Customer Intelligence
                            </Text>
                            {INTELLIGENCE_GROUPS.map(({ label, pages }) => {
                                const icon = GROUP_ICONS[label];
                                if (pages.length === 1) {
                                    return (
                                        <SidebarLink
                                            key={label}
                                            label={label}
                                            to={`${intelligenceBase}/${pages[0][1]}`}
                                            icon={icon}
                                            pathname={pathname}
                                        />
                                    );
                                }
                                const isOpen = pages.some(([, path]) =>
                                    pathname.startsWith(
                                        `${intelligenceBase}/${path}`,
                                    ),
                                );
                                return (
                                    <NavLink
                                        key={label}
                                        label={label}
                                        leftSection={
                                            <MantineIcon icon={icon} />
                                        }
                                        defaultOpened={isOpen}
                                        className={classes.link}
                                        childrenOffset={28}
                                    >
                                        {pages.map(([pageLabel, path]) => (
                                            <NavLink
                                                key={path}
                                                component={Link}
                                                to={`${intelligenceBase}/${path}`}
                                                label={pageLabel}
                                                active={pathname.startsWith(
                                                    `${intelligenceBase}/${path}`,
                                                )}
                                                className={classes.link}
                                            />
                                        ))}
                                    </NavLink>
                                );
                            })}
                        </Box>
                    )}

                    {area === 'work' && (
                        <Box className={classes.group}>
                            <Text className={classes.section}>Automation</Text>
                            <SidebarLink
                                label="Templates"
                                to={`${base}/templates`}
                                icon={IconTemplate}
                                pathname={pathname}
                            />
                            {canManage && (
                                <SidebarLink
                                    label="Workflows"
                                    to={`${base}/workflows`}
                                    icon={IconRouteAltLeft}
                                    pathname={pathname}
                                />
                            )}
                            <Box className={classes.menuSlot}>
                                <AutopilotNavButton
                                    projectUuid={projectUuid}
                                    withLabel
                                />
                            </Box>
                        </Box>
                    )}

                    {area === 'data' && (
                        <>
                            <Box className={classes.group}>
                                <Text className={classes.section}>Analyze</Text>
                                {analyzeItems.map((item) => (
                                    <SidebarLink
                                        key={item.to}
                                        {...item}
                                        pathname={pathname}
                                    />
                                ))}
                                {hasMetrics && (
                                    <Box className={classes.menuSlot}>
                                        <MetricsLink
                                            projectUuid={projectUuid}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box className={classes.group}>
                                <Text className={classes.section}>Content</Text>
                                {contentItems.map((item) => (
                                    <SidebarLink
                                        key={item.to}
                                        {...item}
                                        pathname={pathname}
                                    />
                                ))}
                                <Box className={classes.menuSlot}>
                                    <BrowseMenu projectUuid={projectUuid} />
                                </Box>
                            </Box>
                            {canManage && (
                                <Box className={classes.group}>
                                    <Text className={classes.section}>
                                        Sources
                                    </Text>
                                    <SidebarLink
                                        label="Connectors"
                                        to={`/generalSettings/projectManagement/${projectUuid}/connectors`}
                                        icon={IconPlugConnected}
                                        pathname={pathname}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                <Stack
                    gap={2}
                    className={`${classes.footer} ${classes.menuSlot}`}
                >
                    <LearnLink projectUuid={projectUuid} withLabel />
                    <HelpMenu withLabel />
                    <SettingsMenu withLabel />
                </Stack>
            </NavBarPortalContext.Provider>
        </Box>
    );
};
